import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  Shield, Zap, Wifi, Siren, ScanLine, Cloud, Headset, Database,
  ShieldCheck, ArrowRight, Clock, CheckCircle2, Star, Search,
  ChevronLeft, ChevronRight, ArrowUpDown, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LeadCapture } from '../components/LeadCapture';

const ICON_MAP = {
  zap: Zap, wifi: Wifi, shield: ShieldCheck, siren: Siren,
  scan: ScanLine, cloud: Cloud, headset: Headset, database: Database,
};

const CATEGORY_FILTERS = [
  'All Services', 'General IT Support', 'Network & Connectivity',
  'Cybersecurity', 'Cloud / Infrastructure',
];

const SORT_OPTIONS = [
  { value: 'order', label: 'Default' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'popular', label: 'Most Popular' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'name', label: 'A - Z' },
];

const ITEMS_PER_PAGE = 6;

function StarRating({ rating, size = 'sm', count = null }) {
  const stars = [];
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  const sizeClass = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  for (let i = 0; i < 5; i++) {
    if (i < full) {
      stars.push(<Star key={`item-${i}`} className={`${sizeClass} text-amber-400`} fill="currentColor" strokeWidth={0} />);
    } else if (i === full && half) {
      stars.push(
        <span key={`item-${i}`} className="relative inline-block">
          <Star className={`${sizeClass} text-gray-200`} fill="currentColor" strokeWidth={0} />
          <span className="absolute inset-0 overflow-hidden w-1/2">
            <Star className={`${sizeClass} text-amber-400`} fill="currentColor" strokeWidth={0} />
          </span>
        </span>
      );
    } else {
      stars.push(<Star key={`item-${i}`} className={`${sizeClass} text-gray-200`} fill="currentColor" strokeWidth={0} />);
    }
  }

  return (
    <span className="inline-flex items-center gap-1">
      <span className="flex">{stars}</span>
      {count !== null && <span className="text-xs text-gray-400 ml-0.5">({count})</span>}
    </span>
  );
}

export { StarRating };

export default function ServiceCatalog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState('All Services');
  const [sortBy, setSortBy] = useState('order');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageReviews, setPackageReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ avg_rating: 0, total: 0 });

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', ITEMS_PER_PAGE);
      if (sortBy !== 'order') params.set('sort_by', sortBy);
      if (activeCategory !== 'All Services') params.set('category', activeCategory);
      if (searchQuery) params.set('search', searchQuery);

      const { data } = await api.get(`/api/catalog?${params}`);
      setPackages(data.packages || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load catalog:', err);
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, activeCategory, searchQuery]);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  useEffect(() => {
    const pkgId = searchParams.get('package');
    if (pkgId && packages.length > 0) {
      const pkg = packages.find(p => p.id === pkgId);
      if (pkg) openDetail(pkg);
    }
  }, [searchParams, packages]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setPage(1);
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setPage(1);
  };

  const handleSortChange = (val) => {
    setSortBy(val);
    setPage(1);
  };

  const openDetail = async (pkg) => {
    setSelectedPackage(pkg);
    try {
      const { data } = await api.get(`/api/reviews/${pkg.id}`);
      setPackageReviews(data.reviews || []);
      setReviewStats({ avg_rating: data.avg_rating || 0, total: data.total || 0 });
    } catch {
      setPackageReviews([]);
      setReviewStats({ avg_rating: 0, total: 0 });
    }
  };

  const handleBookNow = (pkg) => {
    if (user) {
      navigate(`/tickets/new?package=${pkg.id}`);
    } else {
      navigate(`/register?redirect=/tickets/new&package=${pkg.id}`);
    }
  };

  const formatPrice = (pkg) => {
    if (pkg.pricing_mode === 'quote') return 'Custom Quote';
    if (pkg.pricing_mode === 'hourly') return `$${pkg.price}/hr`;
    return `$${pkg.price}`;
  };

  const isAuthenticated = user && user !== false;

  return (
    <div className="min-h-screen bg-white" data-testid="service-catalog-page">
      {/* Navigation */}
      <nav className="glass-header sticky top-0 z-50 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="catalog-nav-logo">
            <div className="w-8 h-8 bg-[#D4A843] rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight text-gray-900">TechOps</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/services" className="text-sm font-medium text-[#D4A843]">Services</Link>
            <Link to="/#pricing" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">Pricing</Link>
          </div>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link to="/dashboard"><Button className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold text-sm px-5 rounded-lg">Dashboard</Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" className="text-sm font-medium">Sign In</Button></Link>
                <Link to="/register"><Button className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold text-sm px-5 rounded-lg">Get Started</Button></Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero with Search */}
      <section className="bg-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)', backgroundSize: '32px 32px'}} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 lg:py-20">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-sm text-gray-400 hover:text-white transition-colors">Home</Link>
            <span className="text-gray-600">/</span>
            <span className="text-sm text-white">Service Catalog</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-white leading-none mb-4" data-testid="catalog-heading">
            IT Services, Transparent Pricing
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mb-8">
            Browse our pre-defined service packages. Pick what you need, book instantly, and get expert help.
          </p>
          <form onSubmit={handleSearch} className="relative max-w-lg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search services..."
              className="pl-10 pr-20 bg-white/10 border-white/20 text-white placeholder:text-gray-400 rounded-lg focus:bg-white/15"
              data-testid="catalog-search"
            />
            <Button type="submit" size="sm" className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded text-xs px-3" data-testid="catalog-search-btn">
              Search
            </Button>
          </form>
        </div>
      </section>

      {/* Filters + Sort Bar */}
      <section className="border-b border-gray-200 bg-gray-50 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between py-3 gap-4">
            <div className="flex items-center gap-1 overflow-x-auto shrink-0">
              {CATEGORY_FILTERS.map(cat => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeCategory === cat ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-${cat.toLowerCase().replace(/[\s\/]/g, '-')}`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-gray-400">{total} service{total !== 1 ? 's' : ''}</span>
              <Select value={sortBy} onValueChange={handleSortChange}>
                <SelectTrigger className="w-44 h-9 text-xs rounded-lg bg-white" data-testid="sort-select">
                  <ArrowUpDown className="w-3 h-3 mr-1 text-gray-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Catalog Grid */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(ITEMS_PER_PAGE)].map((_, i) => (
                <div key={`item-${i}`} className="animate-pulse border border-gray-200 rounded-lg p-6 h-80">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg mb-4" />
                  <div className="h-6 bg-gray-200 rounded w-2/3 mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-full mb-1" />
                  <div className="h-4 bg-gray-100 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg">No services match your filters.</p>
              <Button variant="ghost" onClick={() => { setSearchInput(''); setSearchQuery(''); setActiveCategory('All Services'); setSortBy('order'); setPage(1); }} className="mt-3 text-[#D4A843]" data-testid="clear-filters-btn">Clear all filters</Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalog-grid">
                {packages.map((pkg, i) => {
                  const IconComponent = ICON_MAP[pkg.icon] || Zap;
                  return (
                    <div
                      key={pkg.id}
                      className={`group relative border rounded-lg p-6 bg-white transition-all duration-200 hover:shadow-lg hover:-translate-y-1 cursor-pointer ${
                        pkg.popular ? 'border-[#D4A843] shadow-sm' : 'border-gray-200'
                      } animate-fade-in`}
                      style={{ animationDelay: `${i * 0.05}s` }}
                      onClick={() => openDetail(pkg)}
                      data-testid={`catalog-card-${pkg.id}`}
                    >
                      {pkg.popular && (
                        <div className="absolute -top-3 left-4">
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#D4A843] text-[#0A0A0A] font-semibold px-2.5 py-1 rounded">
                            <Star className="w-3 h-3" fill="currentColor" /> Popular
                          </span>
                        </div>
                      )}

                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${pkg.popular ? 'bg-[#D4A843]/10' : 'bg-gray-100'}`}>
                          <IconComponent className={`w-6 h-6 ${pkg.popular ? 'text-[#D4A843]' : 'text-gray-600'}`} strokeWidth={1.5} />
                        </div>
                        <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">{pkg.service_type.replace('_', ' ')}</span>
                      </div>

                      <h3 className="font-heading text-lg font-bold text-gray-900 mb-1.5 group-hover:text-[#D4A843] transition-colors">{pkg.name}</h3>

                      {/* Rating */}
                      <div className="mb-2">
                        {pkg.review_count > 0 ? (
                          <StarRating rating={pkg.avg_rating} count={pkg.review_count} />
                        ) : (
                          <span className="text-xs text-gray-300">No reviews yet</span>
                        )}
                      </div>

                      <p className="text-sm text-gray-500 leading-relaxed mb-3 line-clamp-2">{pkg.description}</p>

                      <div className="flex items-center gap-2 mb-4">
                        <Clock className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.5} />
                        <span className="text-xs text-gray-400">{pkg.estimated_duration}</span>
                      </div>

                      <div className="flex items-end justify-between pt-4 border-t border-gray-100">
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">
                            {pkg.pricing_mode === 'quote' ? 'Starting from' : pkg.pricing_mode === 'hourly' ? 'Per hour' : 'Fixed price'}
                          </p>
                          <p className="font-heading text-2xl font-bold tracking-tight text-gray-900">{formatPrice(pkg)}</p>
                        </div>
                        <Button
                          size="sm"
                          className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs px-4"
                          onClick={(e) => { e.stopPropagation(); handleBookNow(pkg); }}
                          data-testid={`book-${pkg.id}`}
                        >
                          Book Now <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10" data-testid="pagination">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded-lg"
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <Button
                      key={p}
                      variant={p === page ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPage(p)}
                      className={`rounded-lg min-w-[36px] ${p === page ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : ''}`}
                      data-testid={`page-${p}-btn`}
                    >
                      {p}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="rounded-lg"
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Detail Modal with Reviews */}
      {selectedPackage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="package-detail-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedPackage(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${selectedPackage.popular ? 'bg-[#D4A843]/10' : 'bg-gray-100'}`}>
                  {(() => { const IC = ICON_MAP[selectedPackage.icon] || Zap; return <IC className={`w-7 h-7 ${selectedPackage.popular ? 'text-[#D4A843]' : 'text-gray-600'}`} strokeWidth={1.5} />; })()}
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold text-gray-900 tracking-tight" data-testid="modal-package-name">{selectedPackage.name}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-400 uppercase tracking-wider">{selectedPackage.category}</span>
                  </div>
                  <div className="mt-1.5">
                    {reviewStats.total > 0 ? (
                      <StarRating rating={reviewStats.avg_rating} size="md" count={reviewStats.total} />
                    ) : (
                      <span className="text-xs text-gray-400">No reviews yet</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{selectedPackage.pricing_mode === 'quote' ? 'Starting from' : selectedPackage.pricing_mode === 'hourly' ? 'Hourly rate' : 'One-time price'}</p>
                    <p className="font-heading text-3xl font-bold tracking-tight text-gray-900">{formatPrice(selectedPackage)}</p>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-400">
                    <Clock className="w-4 h-4" strokeWidth={1.5} />
                    <span className="text-sm">{selectedPackage.estimated_duration}</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 leading-relaxed mb-6">{selectedPackage.description}</p>

              <div className="mb-6">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">What's Included</h3>
                <ul className="space-y-2.5">
                  {(selectedPackage.includes || []).map((item, i) => (
                    <li key={`item-${i}`} className="flex items-start gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" strokeWidth={2} />
                      <span className="text-sm text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Reviews Section */}
              {packageReviews.length > 0 && (
                <div className="mb-6 pt-5 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Customer Reviews</h3>
                    <span className="text-xs text-gray-400">{reviewStats.total} review{reviewStats.total !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {packageReviews.slice(0, 5).map(rev => (
                      <div key={rev.id} className="bg-gray-50 rounded-lg p-3" data-testid={`review-${rev.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-700">{rev.user_name}</span>
                          <StarRating rating={rev.rating} />
                        </div>
                        {rev.comment && <p className="text-xs text-gray-500 leading-relaxed">{rev.comment}</p>}
                        <p className="text-[10px] text-gray-300 mt-1">{new Date(rev.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  className="flex-1 bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg py-3"
                  onClick={() => handleBookNow(selectedPackage)}
                  data-testid="modal-book-btn"
                >
                  Book This Service <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" className="rounded-lg px-4" onClick={() => setSelectedPackage(null)} data-testid="modal-close-btn">
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="py-16 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-3">Don't See What You Need?</h2>
          <p className="text-base text-gray-500 mb-6 max-w-lg mx-auto">Submit a custom request and our team will provide a tailored quote within 24 hours.</p>
          <div className="flex justify-center gap-3">
            <Link to={isAuthenticated ? "/tickets/new" : "/register"} data-testid="catalog-custom-request-btn">
              <Button className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg px-8">Custom Request</Button>
            </Link>
            <Link to="/"><Button variant="outline" className="rounded-lg px-6 border-gray-300 text-gray-700">Back to Home</Button></Link>
          </div>
        </div>
      </section>

      {/* Lead Capture */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12">
        <LeadCapture magnet="custom_quote" page="services" title="Get a Custom Quote" subtitle="Tell us your needs — we'll build a tailored service package and send it to your inbox." variant="inline" />
      </div>

      <footer className="border-t border-gray-200 py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#D4A843] rounded flex items-center justify-center"><Shield className="w-3 h-3 text-white" strokeWidth={2} /></div>
            <span className="font-heading text-sm font-bold text-gray-900">TechOps</span>
          </div>
          <p className="text-xs text-gray-400">2026 TechOps. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
